import React, { useState, useMemo } from 'react';
import { CHARACTER_STRUCTURES, BIO_PHYSICAL_MARKERS } from '../../utils/textAnalysis';
import AmbientBackground from '../common/AmbientBackground';

// ─── Palette colori per struttura ─────────────────────────────────────────────
const STRUCTURE_PALETTE = {
    schizoid:     { bar: '#64748b', badge: 'bg-slate-100 text-slate-700',   glow: 'shadow-slate-200' },
    oral:         { bar: '#60a5fa', badge: 'bg-blue-50 text-blue-700',      glow: 'shadow-blue-100' },
    symbiotic:    { bar: '#a78bfa', badge: 'bg-violet-50 text-violet-700',  glow: 'shadow-violet-100' },
    psychopathic: { bar: '#f97316', badge: 'bg-orange-50 text-orange-700',  glow: 'shadow-orange-100' },
    masochistic:  { bar: '#ef4444', badge: 'bg-red-50 text-red-700',        glow: 'shadow-red-100' },
    rigid:        { bar: '#10b981', badge: 'bg-emerald-50 text-emerald-700',glow: 'shadow-emerald-100' },
};

// ─── Testi "teaser" per il risultato (non clinici, orientati alla curiosità) ──
const RESULT_TEASER = {
    schizoid:     'Sembra che il tuo corpo tenda a trattenere l\'energia verso il centro, creando una specie di "scudo" interiore. Incuriosente, vero?',
    oral:         'Il corpo segnala un forte bisogno di connessione e nutrimento emotivo. Ci sono pattern da esplorare insieme.',
    symbiotic:    'Il tuo schema corporeo suggerisce una grande sensibilità alle relazioni e ai legami. Vale la pena approfondire.',
    psychopathic: 'La struttura corporea evidenzia energia concentrata verso l\'alto: controllo, immagine, forza. Cosa nasconde?',
    masochistic:  'Il corpo porta tracce di una tensione compressa, come qualcosa che vuole uscire ma non riesce. Parliamone.',
    rigid:        'Struttura atletica ma con una corazza: il corpo ti protegge, forse anche dall\'emozione. Cosa c\'è sotto?',
};

const PublicSomaticExperiment = ({ onBack }) => {
    const [step, setStep] = useState('intro'); // intro | experiment | results
    const [answers, setAnswers] = useState({});

    const totalMarkers = useMemo(
        () => BIO_PHYSICAL_MARKERS.reduce((acc, d) => acc + d.markers.length, 0),
        []
    );

    const toggleMarker = (markerId) => {
        setAnswers(prev => {
            const next = { ...prev };
            if (next[markerId]) delete next[markerId];
            else next[markerId] = true;
            return next;
        });
    };

    const calculateResults = () => {
        const scores = {};
        const totalPossible = {};

        BIO_PHYSICAL_MARKERS.forEach(district => {
            district.markers.forEach(m => {
                m.structures.forEach(sId => {
                    totalPossible[sId] = (totalPossible[sId] || 0) + 1;
                });
            });
        });

        Object.keys(answers).forEach(markerId => {
            for (const district of BIO_PHYSICAL_MARKERS) {
                const marker = district.markers.find(m => m.id === markerId);
                if (marker) {
                    marker.structures.forEach(sId => {
                        scores[sId] = (scores[sId] || 0) + 1;
                    });
                    break;
                }
            }
        });

        return Object.keys(totalPossible)
            .map(sId => {
                const structure = CHARACTER_STRUCTURES.find(s => s.id === sId);
                const count = scores[sId] || 0;
                const possible = totalPossible[sId] || 1;
                return {
                    ...structure,
                    score: count,
                    possible,
                    percentage: Math.round((count / possible) * 100)
                };
            })
            .sort((a, b) => b.percentage - a.percentage);
    };

    // ── INTRO ──────────────────────────────────────────────────────────────────
    if (step === 'intro') {
        return (
            <div className="min-h-screen flex flex-col animate-fade-in relative overflow-hidden bg-white">
                <AmbientBackground />

                <nav className="relative z-20 px-8 py-6 flex justify-between items-center max-w-5xl mx-auto w-full">
                    <div className="flex items-center gap-3">
                        <img src="/logo.png" alt="Logo" className="h-10 w-auto" />
                        <span className="text-xl font-black text-slate-800 tracking-tighter">AwarenessTool</span>
                    </div>
                    <button
                        onClick={onBack}
                        className="px-6 py-2 border-2 border-slate-800 rounded-full text-sm font-bold hover:bg-slate-800 hover:text-white transition-all"
                    >
                        ← Torna indietro
                    </button>
                </nav>

                <main className="relative z-10 flex-1 flex flex-col items-center justify-center px-6 py-12 text-center">
                    <div className="max-w-3xl w-full">
                        <div className="inline-block px-4 py-1.5 bg-emerald-50 border border-emerald-200 rounded-full mb-8">
                            <span className="text-[10px] font-black text-emerald-700 uppercase tracking-[0.2em]">Esperienza Gratuita</span>
                        </div>

                        <h1 className="text-5xl md:text-6xl font-black text-slate-800 mb-6 leading-tight tracking-tight">
                            🧬 Bioenergeticamente<br />
                            <span className="text-emerald-600 italic font-light">Te</span>
                        </h1>

                        <p className="text-xl text-slate-500 mb-4 max-w-2xl mx-auto leading-relaxed">
                            Il corpo racconta storie che la mente non sa ancora di avere.
                            Questo breve esperimento ti aiuta a <strong className="text-slate-700">osservare alcuni segnali fisici</strong> e a scoprire che tipo di struttura caratteriale emergono.
                        </p>
                        <p className="text-sm text-slate-400 mb-12 max-w-xl mx-auto">
                            Nessun login richiesto · Nessun dato salvato · Risultati solo orientativi
                        </p>

                        <div className="grid md:grid-cols-3 gap-6 mb-12 text-left">
                            {[
                                { icon: '👁️', title: 'Osserva', text: 'Seleziona le caratteristiche fisiche e posturali che riconosci in te.' },
                                { icon: '🧮', title: 'Calcola', text: 'L\'algoritmo bioenergetico elabora un profilo caratteriale in tempo reale.' },
                                { icon: '💌', title: 'Scopri', text: 'Ottieni un\'interpretazione e, se ti incuriosisce, contattaci per approfondire.' },
                            ].map(item => (
                                <div key={item.title} className="bg-slate-50 rounded-[2rem] p-6 border border-slate-100">
                                    <div className="text-3xl mb-3">{item.icon}</div>
                                    <h3 className="font-black text-slate-800 mb-2">{item.title}</h3>
                                    <p className="text-sm text-slate-500 leading-relaxed">{item.text}</p>
                                </div>
                            ))}
                        </div>

                        <button
                            onClick={() => setStep('experiment')}
                            className="px-14 py-5 bg-emerald-600 text-white rounded-[2rem] font-black text-lg shadow-2xl shadow-emerald-100 hover:bg-emerald-700 active:scale-95 transition-all"
                        >
                            Inizia l'Esperienza →
                        </button>

                        <p className="mt-6 text-xs text-slate-300 uppercase tracking-widest">
                            Basato sulla metodologia BioGestalt®
                        </p>
                    </div>
                </main>
            </div>
        );
    }

    // ── EXPERIMENT ─────────────────────────────────────────────────────────────
    if (step === 'experiment') {
        const selectedCount = Object.keys(answers).length;
        return (
            <div className="min-h-screen bg-slate-50 relative overflow-hidden">
                <AmbientBackground />

                {/* Sticky header */}
                <div className="sticky top-0 z-30 bg-white/90 backdrop-blur-md border-b border-slate-100 px-6 py-4">
                    <div className="max-w-6xl mx-auto flex items-center justify-between gap-4">
                        <div>
                            <h2 className="text-lg font-black text-slate-800">🧬 Calibrazione Somatica</h2>
                            <p className="text-xs text-slate-400">Seleziona i marker che riconosci in te</p>
                        </div>
                        <div className="flex items-center gap-4">
                            <span className="text-sm font-bold text-slate-500">
                                <span className="text-emerald-600 text-xl font-black">{selectedCount}</span> / {totalMarkers}
                            </span>
                            <button
                                onClick={() => setStep('results')}
                                disabled={selectedCount === 0}
                                className="px-6 py-3 bg-emerald-600 text-white rounded-2xl font-black text-sm shadow-lg shadow-emerald-100 hover:bg-emerald-700 disabled:opacity-40 disabled:cursor-not-allowed active:scale-95 transition-all"
                            >
                                Elabora Profilo →
                            </button>
                        </div>
                    </div>
                </div>

                <div className="max-w-6xl mx-auto px-6 py-10 relative z-10">
                    <div className="bg-amber-50 border border-amber-200 rounded-2xl px-5 py-3 mb-8 flex items-start gap-3">
                        <span className="text-amber-500 text-lg mt-0.5">💡</span>
                        <p className="text-sm text-amber-700">
                            <strong>Suggerimento:</strong> Guarda il tuo corpo in uno specchio oppure chiediti come lo descriveresti a qualcuno che non ti conosce. Seleziona solo ciò che è <em>abituale</em> in te, non occasionale.
                        </p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {BIO_PHYSICAL_MARKERS.map((district) => (
                            <div key={district.district} className="bg-white p-6 rounded-[2rem] border border-slate-100 shadow-sm hover:shadow-md transition-all">
                                <h3 className="text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] mb-5 flex items-center gap-2">
                                    <span className="w-1.5 h-1.5 bg-emerald-400 rounded-full"></span>
                                    {district.district}
                                </h3>
                                <div className="grid gap-2">
                                    {district.markers.map(marker => {
                                        const isSelected = !!answers[marker.id];
                                        return (
                                            <button
                                                key={marker.id}
                                                onClick={() => toggleMarker(marker.id)}
                                                className={`w-full text-left p-3.5 rounded-2xl text-[11px] font-bold transition-all border-2 flex items-center justify-between group ${
                                                    isSelected
                                                        ? 'bg-emerald-600 text-white border-emerald-700 shadow-md'
                                                        : 'bg-slate-50 text-slate-600 border-slate-50 hover:border-emerald-200 hover:bg-emerald-50/30'
                                                }`}
                                            >
                                                <span className="flex-1 leading-tight pr-3">{marker.label}</span>
                                                <div className={`w-5 h-5 min-w-[20px] rounded-full border-2 flex items-center justify-center transition-all ${
                                                    isSelected ? 'bg-white border-white' : 'border-slate-200 group-hover:border-emerald-300'
                                                }`}>
                                                    {isSelected && <span className="text-emerald-600 text-[10px] font-black">✓</span>}
                                                </div>
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>
                        ))}
                    </div>

                    <div className="mt-10 flex flex-col sm:flex-row justify-between items-center gap-4">
                        <button
                            onClick={() => { setAnswers({}); setStep('intro'); }}
                            className="text-slate-400 font-bold hover:text-slate-600 text-sm uppercase tracking-widest transition-colors"
                        >
                            ← Ricomincia
                        </button>
                        <button
                            onClick={() => setStep('results')}
                            disabled={selectedCount === 0}
                            className="px-16 py-5 bg-emerald-600 text-white rounded-[2rem] font-black text-xl shadow-2xl shadow-emerald-100 hover:bg-emerald-700 disabled:opacity-40 disabled:cursor-not-allowed active:scale-95 transition-all"
                        >
                            Genera Profilo Caratteriale
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    // ── RESULTS ────────────────────────────────────────────────────────────────
    const finalResults = calculateResults();
    const dominant = finalResults[0];
    const palette = STRUCTURE_PALETTE[dominant?.id] || { bar: '#10b981', badge: 'bg-emerald-50 text-emerald-700', glow: 'shadow-emerald-100' };
    const teaser = dominant ? (RESULT_TEASER[dominant.id] || '') : '';

    return (
        <div className="min-h-screen bg-slate-50 relative overflow-hidden">
            <AmbientBackground />

            <div className="max-w-3xl mx-auto px-6 py-12 relative z-10">

                {/* Header risultati */}
                <div className="text-center mb-10">
                    <div className="inline-block px-4 py-1.5 bg-emerald-50 border border-emerald-200 rounded-full mb-6">
                        <span className="text-[10px] font-black text-emerald-700 uppercase tracking-[0.2em]">Il tuo Profilo Somatico</span>
                    </div>
                    <h1 className="text-4xl font-black text-slate-800 tracking-tight mb-3">Ecco cosa emerge</h1>
                    <p className="text-slate-500 max-w-md mx-auto">
                        Questi risultati sono indicativi e orientativi, non diagnostici. Rappresentano un punto di partenza per l'esplorazione.
                    </p>
                </div>

                {/* Card dominante */}
                {dominant && (
                    <div className={`bg-white rounded-[2.5rem] border border-slate-100 shadow-xl ${palette.glow} p-8 mb-6`}>
                        <div className="flex items-start justify-between mb-4">
                            <div>
                                <span className={`text-[9px] font-black uppercase tracking-widest px-3 py-1 rounded-full ${palette.badge}`}>
                                    Struttura Dominante
                                </span>
                                <h2 className="text-3xl font-black text-slate-800 mt-3 tracking-tight">{dominant.name}</h2>
                                <p className="text-xs text-slate-400 font-bold italic mt-1">Ferita di base: {dominant.wound}</p>
                            </div>
                            <span className="text-5xl font-black" style={{ color: palette.bar }}>{dominant.percentage}%</span>
                        </div>
                        <div className="w-full bg-slate-100 h-3 rounded-full overflow-hidden mb-4">
                            <div
                                className="h-full rounded-full transition-all duration-1000"
                                style={{ width: `${dominant.percentage}%`, backgroundColor: palette.bar }}
                            />
                        </div>
                        <p className="text-slate-600 text-sm leading-relaxed italic bg-slate-50 rounded-2xl px-5 py-4 border border-slate-100">
                            "{teaser}"
                        </p>
                    </div>
                )}

                {/* Altre strutture */}
                <div className="grid gap-3 mb-10">
                    {finalResults.slice(1).map((res) => {
                        const p = STRUCTURE_PALETTE[res.id] || { bar: '#94a3b8' };
                        return (
                            <div key={res.id} className="bg-white rounded-2xl border border-slate-100 px-6 py-4 flex items-center gap-4">
                                <div className="flex-1">
                                    <div className="flex items-center justify-between mb-1.5">
                                        <span className="font-bold text-slate-700 text-sm">{res.name}</span>
                                        <span className="font-black text-slate-500 text-sm">{res.percentage}%</span>
                                    </div>
                                    <div className="w-full bg-slate-100 h-2 rounded-full overflow-hidden">
                                        <div
                                            className="h-full rounded-full transition-all duration-700"
                                            style={{ width: `${res.percentage}%`, backgroundColor: p.bar }}
                                        />
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>

                {/* Disclaimer */}
                <div className="bg-amber-50 border border-amber-200 rounded-2xl px-6 py-4 mb-10 flex gap-3">
                    <span className="text-amber-500 text-lg">⚠️</span>
                    <p className="text-xs text-amber-700 leading-relaxed">
                        <strong>Nota importante:</strong> Questa esperienza è uno strumento esplorativo basato sulla metodologia BioGestalt®.
                        I risultati non hanno valore diagnostico e non sostituiscono un percorso con un professionista qualificato.
                        La struttura caratteriale è un <em>orizzonte di lavoro</em>, non un'etichetta.
                    </p>
                </div>

                {/* CTA principale */}
                <div className="bg-[#335d73] rounded-[2.5rem] p-10 text-white shadow-2xl relative overflow-hidden text-center">
                    <div className="absolute top-0 right-0 w-48 h-48 bg-white/10 blur-[80px] rounded-full -mr-24 -mt-24"></div>
                    <div className="relative z-10">
                        <h2 className="text-2xl font-black mb-3 italic">
                            Curioso di sapere cosa significa davvero?
                        </h2>
                        <p className="text-slate-200 mb-6 text-sm max-w-md mx-auto opacity-90 leading-relaxed">
                            Il profilo somatico è solo l'inizio. In un percorso di counseling esploriamo insieme cosa racconta il corpo
                            e come trasformarlo in una risorsa di crescita.
                        </p>
                        <a
                            href="mailto:info@awarenesstool.it?subject=Bioenergeticamente%20Te%20-%20Voglio%20saperne%20di%20più&body=Ciao%2C%20ho%20completato%20Bioenergeticamente%20Te%20e%20sono%20curioso%2Fa%20di%20approfondire.%20La%20struttura%20dominante%20emersa%20è%3A%20"
                            className="inline-flex items-center gap-3 px-8 py-4 bg-white text-[#335d73] rounded-2xl font-black hover:bg-[#f0f5f8] transition transform hover:-translate-y-1 shadow-lg mb-4"
                        >
                            📩 Scrivici: info@awarenesstool.it
                        </a>
                        <p className="text-[10px] text-slate-300 uppercase tracking-widest">
                            Primo colloquio conoscitivo gratuito
                        </p>
                    </div>
                </div>

                {/* Azioni secondarie */}
                <div className="mt-8 flex flex-col sm:flex-row justify-center gap-4">
                    <button
                        onClick={() => { setAnswers({}); setStep('intro'); }}
                        className="px-8 py-3 text-slate-500 font-bold hover:text-slate-800 bg-white border border-slate-200 rounded-2xl text-sm transition-all hover:shadow-md"
                    >
                        🔄 Rifai il test
                    </button>
                    <button
                        onClick={onBack}
                        className="px-8 py-3 text-slate-500 font-bold hover:text-slate-800 bg-white border border-slate-200 rounded-2xl text-sm transition-all hover:shadow-md"
                    >
                        ← Torna alla pagina principale
                    </button>
                </div>
            </div>
        </div>
    );
};

export default PublicSomaticExperiment;
